'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';

import { useEffect, useState } from 'react';
import { FaFacebook, FaRegEnvelope } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { IoIosLink } from 'react-icons/io';

interface BlogHeaderProps {
  title: string;
  locale: string;
  blog?: Blog;
  darkerBg?: boolean;
}

export function BlogShare({ blog, title, locale, darkerBg }: BlogHeaderProps) {
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, []);

  let formattedDate;

  try {
    if (blog) {
      formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
    }
  } catch (error) {
    if (error instanceof RangeError && blog) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  const socialsToShare = [
    {
      name: 'Facebook',
      icon: <FaFacebook size={20} className="text-[#171717]" />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'X',
      icon: <FaXTwitter size={20} className="text-[#171717]" />,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'Copy',
      icon: <IoIosLink size={20} className="text-[#171717]" />,
      onClick: () => {
        navigator.clipboard.writeText(currentUrl);
        alert('URL copied to clipboard!');
      },
    },
    {
      name: 'Send email',
      icon: <FaRegEnvelope size={20} className="text-[#171717]" />,
      url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(currentUrl)}`,
    },
  ];

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-base font-semibold text-black">{formattedDate}</span>
      <div className="flex flex-col items-start justify-start gap-2.5 sm:flex-row sm:items-center sm:justify-end">
        <p className="text-base font-semibold text-black">Zdieľať článok</p>
        <div className="flex items-center gap-2.5">
          {socialsToShare.map(social => (
            <ShareButton social={social} key={social.name} darkerBg={darkerBg} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShareButton({
  social,
  darkerBg = false,
}: {
  darkerBg?: boolean;
  social: {
    name: string;
    icon: React.ReactNode;
    url?: string;
    onClick?: () => void;
  };
}) {
  return social.url ? (
    <Link
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex min-h-14 min-w-14 items-center justify-center rounded-[8px] transition-all',
        darkerBg ? 'bg-[#F7F7F7]' : 'bg-white',
      )}
    >
      {social.icon}
    </Link>
  ) : (
    <div
      onClick={social.onClick}
      className={cn(
        'flex min-h-14 min-w-14 items-center justify-center rounded-[8px] transition-all',
        darkerBg ? 'bg-[#F7F7F7]' : 'bg-white',
      )}
      aria-label={social.name}
    >
      {social.icon}
    </div>
  );
}
